import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FC, useEffect, useState } from "react";
import {
  ColaboradorPortal,
  EmpresaPortal,
  UsuarioFamily,
  getColaboradoresByEmpresa,
  getEmpresaByColaborador,
  getEmpresasPortal,
  getUserFamilyById,
} from "src/apis/eezyfamily";
import CrudDialog from "src/pages/Admin/EezyFamily/components/CrudDialog";
import { formatDateToBR } from "src/utils/date";

type Props = {
  open: boolean;
  editTarget: UsuarioFamily | null;
  onClose: () => void;
  onSaved: () => void;
  onCreate: (p: Partial<UsuarioFamily>) => Promise<void>;
  onUpdate: (
    collaboratorId: number,
    p: Partial<UsuarioFamily> & { enterprise?: number; target_company_id?: number }
  ) => Promise<void>;
};

type FormState = {
  name: string;
  surname: string;
  email: string;
  phone: string;
  document: string;
  birthday: string;
  postal_code: string;
  add_number: string;
  complement: string;
  role: string;
  token: string;
  li_termos: boolean;
  company_id: string;
  collaborator_id: string;
  password: string;
  confirmPassword: string;
};

const emptyForm: FormState = {
  name: "", surname: "", email: "", phone: "", document: "",
  birthday: "", postal_code: "", add_number: "", complement: "",
  role: "", token: "", li_termos: false,
  company_id: "", collaborator_id: "",
  password: "", confirmPassword: "",
};

const UsuarioModal: FC<Props> = ({
  open,
  editTarget,
  onClose,
  onSaved,
  onCreate,
  onUpdate,
}): JSX.Element => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [jsonText, setJsonText] = useState("{}");
  const [jsonError, setJsonError] = useState("");
  const [originalUser, setOriginalUser] = useState<UsuarioFamily | null>(null);
  const [originalCompanyId, setOriginalCompanyId] = useState<number | undefined>(undefined);
  const [empresas, setEmpresas] = useState<EmpresaPortal[]>([]);
  const [colaboradores, setColaboradores] = useState<ColaboradorPortal[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState("");

  const isEdit = editTarget !== null;

  const setField = (key: keyof FormState, value: string | boolean) =>
    setForm((p) => ({ ...p, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setError("");
    setJsonError("");

    getEmpresasPortal()
      .then(setEmpresas)
      .catch((err) => setError(err?.message ?? "Erro ao carregar empresas."));

    if (!editTarget) {
      setForm(emptyForm);
      setJsonText("{}");
      setOriginalUser(null);
      setOriginalCompanyId(undefined);
      setColaboradores([]);
      return;
    }

    setLoadingData(true);
    const loadEdit = async () => {
      try {
        let user: UsuarioFamily = editTarget;
        try {
          user = await getUserFamilyById(editTarget.id);
        } catch {
          // fallback to list data
        }

        let companyId = user.company_id;
        if (!companyId && user.collaborator_id) {
          const emp = await getEmpresaByColaborador(user.collaborator_id);
          if (emp?.id) companyId = emp.id;
        }

        setOriginalUser(user);
        setOriginalCompanyId(companyId);

        setForm({
          name: user.name ?? "",
          surname: user.surname ?? "",
          email: user.email ?? "",
          phone: user.phone ?? "",
          document: user.document ?? "",
          birthday: user.birthday ? user.birthday.split("T")[0] : "",
          postal_code: user.postal_code ?? "",
          add_number: user.add_number != null ? String(user.add_number) : "",
          complement: user.complement ?? "",
          role: user.role ?? "",
          token: user.token ?? "",
          li_termos: Boolean(user.li_termos),
          company_id: companyId != null ? String(companyId) : "",
          collaborator_id: user.collaborator_id != null ? String(user.collaborator_id) : "",
          password: "",
          confirmPassword: "",
        });

        setJsonText(
          user.dados_assinatura
            ? JSON.stringify(user.dados_assinatura, null, 2)
            : "{}"
        );

        if (companyId) {
          const colabs = await getColaboradoresByEmpresa(companyId);
          setColaboradores(colabs);
        }
      } catch (err: any) {
        setError(err?.message ?? "Erro ao carregar dados do usuário.");
      } finally {
        setLoadingData(false);
      }
    };
    loadEdit();
  }, [open, editTarget]);

  useEffect(() => {
    const cid = Number(form.company_id);
    if (!cid) {
      setColaboradores([]);
      return;
    }
    getColaboradoresByEmpresa(cid)
      .then(setColaboradores)
      .catch(() => setColaboradores([]));
  }, [form.company_id]);

  const handleJsonChange = (value: string) => {
    setJsonText(value);
    try {
      JSON.parse(value);
      setJsonError("");
    } catch {
      setJsonError("JSON inválido");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (jsonError) return;
    if (form.password && form.password !== form.confirmPassword) {
      setError("A confirmação de senha não confere.");
      return;
    }
    setLoading(true);
    try {
      let dados_assinatura: object | null = null;
      try {
        dados_assinatura = JSON.parse(jsonText);
      } catch {
        dados_assinatura = null;
      }

      if (isEdit && editTarget && originalUser) {
        const payload: Partial<UsuarioFamily> & { enterprise?: number; target_company_id?: number } = {
          name: form.name || originalUser.name,
          surname: form.surname || originalUser.surname,
          email: form.email || originalUser.email,
          phone: form.phone || originalUser.phone,
          document: form.document || originalUser.document,
          birthday: form.birthday || originalUser.birthday,
          postal_code: form.postal_code || originalUser.postal_code,
          add_number: form.add_number ? Number(form.add_number) : originalUser.add_number,
          complement: form.complement || originalUser.complement,
          role: form.role || originalUser.role,
          token: form.token || originalUser.token,
          li_termos: form.li_termos,
          collaborator_id: form.collaborator_id ? Number(form.collaborator_id) : originalUser.collaborator_id,
          dados_assinatura,
        };

        const newCompanyId = Number(form.company_id) || undefined;
        if (newCompanyId && newCompanyId !== originalCompanyId) {
          payload.enterprise = originalCompanyId;
          payload.target_company_id = newCompanyId;
        } else {
          payload.enterprise = newCompanyId ?? originalCompanyId;
        }

        if (form.password.trim()) {
          (payload as any).password = form.password;
        }

        const collaboratorId = Number(form.collaborator_id) || Number(originalUser.collaborator_id);
        await onUpdate(collaboratorId, payload);
      } else {
        const payload: Partial<UsuarioFamily> & { enterprise?: number } = {
          ...(form.name && { name: form.name }),
          ...(form.surname && { surname: form.surname }),
          ...(form.email && { email: form.email }),
          ...(form.phone && { phone: form.phone }),
          ...(form.document && { document: form.document }),
          ...(form.birthday && { birthday: form.birthday }),
          ...(form.postal_code && { postal_code: form.postal_code }),
          ...(form.add_number && { add_number: Number(form.add_number) }),
          ...(form.complement && { complement: form.complement }),
          ...(form.role && { role: form.role }),
          ...(form.token && { token: form.token }),
          li_termos: form.li_termos,
          ...(form.collaborator_id && { collaborator_id: Number(form.collaborator_id) }),
          dados_assinatura,
        };
        if (form.company_id) {
          payload.company_id = Number(form.company_id);
          payload.enterprise = Number(form.company_id);
        }
        if (form.password) {
          (payload as any).password = form.password;
        }
        await onCreate(payload);
      }

      onSaved();
    } catch (err: any) {
      setError(err?.message ?? "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const section = (label: string) => (
    <>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        {label}
      </Typography>
      <Divider />
    </>
  );

  return (
    <CrudDialog
      open={open}
      title={isEdit ? `Editar Usuário — ${editTarget?.name}` : "Novo Usuário"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading || loadingData}
      maxWidth="md"
    >
      {error && (
        <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>{error}</Box>
      )}

      {/* Identificação (somente leitura na edição) */}
      {isEdit && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Identificação</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormGroup sx={{ flex: 1 }}>
                <FormLabel>ID</FormLabel>
                <TextField value={editTarget?.id ?? ""} disabled size="small" />
              </FormGroup>
              <FormGroup sx={{ flex: 1 }}>
                <FormLabel>Criado em</FormLabel>
                <TextField
                  value={editTarget?.created_at ? formatDateToBR(editTarget.created_at) : ""}
                  disabled
                  size="small"
                />
              </FormGroup>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Dados Pessoais */}
      {section("Dados Pessoais")}
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel required>Nome</FormLabel>
          <TextField
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            size="small"
            autoFocus
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel required>Sobrenome</FormLabel>
          <TextField
            value={form.surname}
            onChange={(e) => setField("surname", e.target.value)}
            size="small"
          />
        </FormGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel required>E-mail</FormLabel>
          <TextField
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            size="small"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Telefone</FormLabel>
          <TextField
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="(11) 99999-9999"
            size="small"
          />
        </FormGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Documento (CPF/CNPJ)</FormLabel>
          <TextField
            value={form.document}
            onChange={(e) => setField("document", e.target.value)}
            size="small"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Data de Nascimento</FormLabel>
          <TextField
            type="date"
            value={form.birthday}
            onChange={(e) => setField("birthday", e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </FormGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>CEP</FormLabel>
          <TextField
            value={form.postal_code}
            onChange={(e) => setField("postal_code", e.target.value)}
            size="small"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Número</FormLabel>
          <TextField
            type="number"
            value={form.add_number}
            onChange={(e) => setField("add_number", e.target.value)}
            size="small"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 2 }}>
          <FormLabel>Complemento</FormLabel>
          <TextField
            value={form.complement}
            onChange={(e) => setField("complement", e.target.value)}
            size="small"
          />
        </FormGroup>
      </Box>

      {/* Acesso */}
      {section("Acesso e Papel")}
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Papel (Role)</FormLabel>
          <TextField
            value={form.role}
            onChange={(e) => setField("role", e.target.value)}
            placeholder="ADMIN, COLABORADOR, PACIENTE..."
            size="small"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Token</FormLabel>
          <TextField
            value={form.token}
            onChange={(e) => setField("token", e.target.value)}
            size="small"
          />
        </FormGroup>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.li_termos}
            onChange={(e) => setField("li_termos", e.target.checked)}
          />
        }
        label="Aceitou os termos?"
      />

      {/* Empresa e Colaborador */}
      {section("Empresa e Colaborador")}
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Empresa</FormLabel>
          <Select
            value={form.company_id}
            onChange={(e) => {
              setField("company_id", e.target.value as string);
              setField("collaborator_id", "");
            }}
            displayEmpty
            size="small"
          >
            <MenuItem value="">Selecionar...</MenuItem>
            {empresas.map((emp) => (
              <MenuItem key={emp.id} value={String(emp.id)}>
                {emp.trade_name}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Colaborador</FormLabel>
          <Select
            value={form.collaborator_id}
            onChange={(e) => setField("collaborator_id", e.target.value as string)}
            displayEmpty
            disabled={!form.company_id}
            size="small"
          >
            <MenuItem value="">Selecionar...</MenuItem>
            {colaboradores.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
      </Box>

      {/* Dados de Assinatura */}
      {section("Dados de Assinatura")}
      <FormGroup>
        <FormLabel>Dados de Assinatura (JSON)</FormLabel>
        <TextField
          multiline
          minRows={4}
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          inputProps={{ style: { fontFamily: "monospace", fontSize: "0.8rem" } }}
          error={Boolean(jsonError)}
        />
        {jsonError && (
          <FormHelperText error>{jsonError}</FormHelperText>
        )}
      </FormGroup>

      {/* Senha */}
      {section("Senha")}
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>{isEdit ? "Nova Senha" : "Senha"}</FormLabel>
          <TextField
            type="password"
            value={form.password}
            onChange={(e) => setField("password", e.target.value)}
            size="small"
            helperText={isEdit ? "Deixar em branco para não alterar" : ""}
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Confirmar Senha</FormLabel>
          <TextField
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setField("confirmPassword", e.target.value)}
            size="small"
            error={Boolean(form.password && form.confirmPassword && form.password !== form.confirmPassword)}
            helperText={
              form.password && form.confirmPassword && form.password !== form.confirmPassword
                ? "Senhas não conferem"
                : ""
            }
          />
        </FormGroup>
      </Box>
    </CrudDialog>
  );
};

export default UsuarioModal;
